import { useCallback, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { withNamespaces } from "react-i18next";
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import overlayFactory from "react-bootstrap-table2-overlay";
import { EMPTY_LIST } from "src/common/constants";
import DataProtectionTomsService from "src/modules/data-protection/apis/DataProtectionTomsService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TOM_STATUS_LIST } from "src/modules/data-protection/constants";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";
import DateUtils from "src/services/utils/DateUtils";
import "./style.scss";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import FilteringComponent from "../../components/filtering-component";
import CloneModal from "../../components/CloneModal";
import { getTomDisplayId, getTomId } from "../../utils/tomIdentifiers";

const ListToms = ({ t }) => {
  const history = useHistory();
  const dateUtils = new DateUtils();
  const [moreActionsDropDownOpen, setMoreActionsDropDownOpen] = useState(false);
  const toggleMoreActionsDropDown = () =>
    setMoreActionsDropDownOpen(!moreActionsDropDownOpen);
  const [selectedTOMToClone, setSelectedTOMToClone] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    pageIndex: 1,
    pageSize: 25,
    sortField: null,
    sortOrder: null,
  });
  const handleFetchTOMsListQuery = useQuery({
    queryKey: ["data-protection-toms-list", tableFilters],
    queryFn: async () => {
      const service = DataProtectionTomsService.getInstance();
      return await service.list({
        ...tableFilters,
      });
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while retrieving the TOMs."), {
        type: "error",
      });
    },
  });

  const toCamelCase = (str) =>
    str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

  const handleTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        const { page, sizePerPage } = data;
        setTableFilters({
          ...tableFilters,
          pageIndex: page,
          pageSize: sizePerPage,
        });
        break;

      case "sort": {
        const { sortField, sortOrder } = data;
        let normalizedSortField;
        if (sortField === "secondary_id" || sortField === "secondaryId") {
          normalizedSortField = "secondaryId";
        } else {
          normalizedSortField = toCamelCase(sortField);
        }

        setTableFilters({
          ...tableFilters,
          sortField: normalizedSortField,
          sortOrder: sortOrder,
        });
        break;
      }
      default:
        return false;
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setTableFilters((currentFilters) => {
      return {
        ...currentFilters,
        ...newFilters,
        pageIndex: 1,
      };
    });
  }, []);

  const handleRenderStatus = (status) => {
    const statusData = TOM_STATUS_LIST[status.toUpperCase()];
    if (!statusData) {
      return "---";
    }

    return (
      <div
        style={{
          fontSize: "10px",
          fontWeight: 700,
          padding: "3px 8px",
          background: statusData.backgroundColor,
          opacity: statusData?.opacity || 1,
          textAlign: "center",
          color: "#FFFFFF",
          borderRadius: "7px",
          whiteSpace: "nowrap",
          width: "78.83px",
          height: "21.59px",
        }}
      >
        {t(statusData.title)}
      </div>
    );
  };

  const tableColumns = useMemo(() => {
    return [
      {
        dataField: "secondaryId",
        text: t("ID"),
        sort: true,
        key: 100,
        style: {
          width: "80px",
        },
        formatter: (_cell, row) => (
          <Link
            to={`/admin/data-protection/toms/${getTomId(row)}/details`}
            // className="text-primary"
          >
            {getTomDisplayId(row)}
          </Link>
        ),
      },
      {
        dataField: "name",
        text: t("Name"),
        sort: true,
        key: 0,
        style: {
          width: "80px",
        },
        formatter: (cell, row) => (
          <Link
            to={`/admin/data-protection/toms/${getTomId(row)}/details`}
          >
            {cell}
          </Link>
        ),
      },
      {
        dataField: "status",
        text: t("Status"),
        sort: true,
        key: 1,
        style: {
          width: "140px",
        },
        formatter: (cell, row) => {
          return <p>{handleRenderStatus(row.status)}</p>;
        },
      },
      {
        dataField: "next_revision",
        text: t("Next Revision"),
        sort: true,
        key: 2,
        style: {
          width: "160px",
        },
        formatter: (cell) => {
          return dateUtils.convertTimestampToOnlyDate(
            cell,
            INTERNATIONAL_DATE_FORMAT
          );
        },
      },
      {
        dataField: "analyst_assigned_object",
        text: t("Assigned Analyst"),
        sort: false,
        key: 3,
        style: {
          width: "180px",
        },
        formatter: (cellContent) => {
          if (!cellContent || Array.isArray(cellContent)) return null;
          if (cellContent?.avatar && cellContent?.avatar !== "") {
            return (
              <div className="d-flex gap-1 align-items-center">
                <div className="avatar-xs ">
                  <span
                    className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
                    style={{ overflow: "hidden" }}
                  >
                    <img
                      alt={cellContent?.name}
                      src={cellContent?.avatar}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        objectFit: "cover",
                      }}
                    />
                  </span>
                </div>{" "}
                {`${cellContent?.name}`}
              </div>
            );
          }
          return (
            <div>
              <span className="dt-list-col-bullet">
                {cellContent.name?.[0]}
              </span>{" "}
              <span>{`${cellContent?.name}`}</span>
            </div>
          );
        },
      },
      {
        dataField: "created_at",
        text: t("Created On"),
        sort: true,
        key: 5,
        style: {
          width: "170px",
        },
        formatter: (cell) => {
          return dateUtils.convertTimestampToOnlyDate(
            cell,
            INTERNATIONAL_DATE_FORMAT
          );
        },
      },
      {
        dataField: "actions",
        text: t("Action"),
        sort: false,
        key: 6,
        style: {
          width: "120px",
        },
        formatter: (cellContent, row) => {
          const tomId = getTomId(row);

          return (
            <div className="actions">
              <div id={`clone-button-${tomId}`}>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  className="border-0"
                  onClick={() => {
                    setSelectedTOMToClone(row);
                  }}
                >
                  <i class="ri-file-copy-line"></i>
                </Button>
                <UncontrolledTooltip target={`clone-button-${tomId}`}>
                  {t("Clone")}
                </UncontrolledTooltip>
              </div>
            </div>
          );
        },
      },
    ];
  });

  const NoDataIndication = () =>
    handleFetchTOMsListQuery.isFetched && !handleFetchTOMsListQuery.length ? (
      <div className="alert m-0" role="alert">
        <p
          style={{
            textAlign: "center",
            marginBottom: 0,
          }}
        >
          {t(EMPTY_LIST)}
        </p>
      </div>
    ) : (
      <></>
    );

  const hideModal = () => setShowCloneModal(!showCloneModal);

  useEffect(() => {
    if (selectedTOMToClone) {
      setShowCloneModal(true);
    }
  }, [selectedTOMToClone]);

  useEffect(() => {
    if (!showCloneModal) {
      setSelectedTOMToClone(null);
    }
  }, [showCloneModal]);

  return (
    <Row>
      <CloneModal
        showModal={showCloneModal}
        selectedTom={selectedTOMToClone}
        hideModal={hideModal}
      />
      <Col sm="12">
        <PaginationProvider
          pagination={paginationFactory({
            custom: true,
            page: tableFilters.pageIndex,
            sizePerPage: tableFilters.pageSize,
            totalSize: parseInt(handleFetchTOMsListQuery.data?.itemsCount || 0),
            withFirstAndLast: false,
            alwaysShowAllBtns: true,
            prePageText: (
              <span>
                <i className="ri-arrow-left-s-line"></i> {t("Back")}
              </span>
            ),
            nextPageText: (
              <span>
                {t("Next")} <i className="ri-arrow-right-s-line"></i>
              </span>
            ),
            prePageTitle: t("Pre page"),
            firstPageTitle: t("Next page"),
            sizePerPageList: [
              {
                text: "25",
                value: 25,
              },
              {
                text: "50",
                value: 50,
              },
            ],
            showTotal: false,
            paginationTotalRenderer: (from, to, size) => {
              return (
                <span className="react-bootstrap-table-pagination-total">
                  {`${t("Total Items")} ${size}`}
                </span>
              );
            },
            hideSizePerPage: false,
          })}
        >
          {({ paginationProps, paginationTableProps }) => (
            <>
              <Row className="mb-5 mt-3">
                <Col sm="12">
                  <div className="d-flex gap-2 justify-content-end me-2">
                    <FilteringComponent onChange={handleFilterChange} />

                    <Dropdown
                      isOpen={moreActionsDropDownOpen}
                      toggle={toggleMoreActionsDropDown}
                    >
                      <DropdownToggle caret variant="outline-secondary" outline>
                        {t("Actions")}
                      </DropdownToggle>
                      <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem
                          onClick={() => {
                            history.push("/admin/data-protection/toms/create");
                          }}
                        >
                          <i className="ri-add-line align-middle me-2" />
                          {t("Add A New TOM")}
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col sm="12">
                  <BootstrapTable
                    remote={{
                      pagination: true,
                      filter: false,
                      sort: true,
                      cellEdit: false,
                      search: false,
                    }}
                    loading={
                      handleFetchTOMsListQuery.isFetching ||
                      handleFetchTOMsListQuery.isLoading
                    }
                    overlay={overlayFactory({
                      spinner: (
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="md"
                        />
                      ),
                      text: "Loading...",
                    })}
                    onTableChange={handleTableChange}
                    defaultSorted={[]}
                    keyField={"tom"}
                    responsive
                    bordered={false}
                    data={handleFetchTOMsListQuery.data?.toms || []}
                    striped={false}
                    columns={tableColumns}
                    wrapperClasses="table-responsive"
                    classes={"table tpdd-table"}
                    headerWrapperClasses={"thead-light"}
                    style={{
                      overflowX: "auto",
                    }}
                    noDataIndication={() => <NoDataIndication />}
                    {...paginationTableProps}
                  />
                </Col>
              </Row>

              <Row>
                <Col sm="12">
                  <div className="tpdd-pagination-style-1">
                    <PaginationTotalStandalone {...paginationProps} />

                    <div>
                      <PaginationListStandalone {...paginationProps} />

                      <SizePerPageDropdownStandalone {...paginationProps} />
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </PaginationProvider>
      </Col>
    </Row>
  );
};

export default withNamespaces()(ListToms);
